import formImage from "img/partners/form-image.webp";
import React, { useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { CheckoutErrorModal } from "web/react/components/checkout/v2/checkout-error-modal/checkout-error-modal";
import { Button } from "web/react/emo/button/button";
import { Heading } from "web/react/emo/heading/heading";
import { TextInput } from "web/react/emo/text-input/text-input";
import { Text } from "web/react/emo/text/text";
import { useToast } from "web/react/emo/toast/useToast";
import { useSectionImpression } from "web/react/pages/partners/use-section-impression";
import analytics from "web/script/analytics/analytics";
import { gettext, gettextNoop } from "web/script/modules/django-i18n";
import requester from "web/script/modules/requester";
import logging from "web/script/utils/logging";
import * as styles from "./partners-contact.css";

const FORM_URL = "/partners/";

const FIELD_LABELS = {
    name: gettextNoop("partners.contact.field_name"),
    email: gettextNoop("partners.contact.field_email"),
    company: gettextNoop("partners.contact.field_company"),
    message: gettextNoop("partners.contact.field_message"),
};

const FIELD_ANALYTICS_LABELS: Partial<Record<keyof FormValues, string>> = {
    name: "name",
    email: "email",
    company: "company",
    message: "message",
};

interface FormValues {
    name: string;
    email: string;
    company: string;
    message: string;
    hp_field?: string;
}

export default function PartnersContact(): JSX.Element {
    const sectionRef = useSectionImpression("contact_form", 8);
    const toast = useToast();
    const [showErrorModal, setShowErrorModal] = useState(false);
    const changedFields = useRef(new Set<string>());

    const {
        register,
        handleSubmit,
        watch,
        formState: { errors, isValid, isSubmitting },
    } = useForm<FormValues>({ mode: "all", defaultValues: { hp_field: "" } });

    useEffect(() => {
        const subscription = watch((_, { name }) => {
            if (name && !changedFields.current.has(name)) {
                const label = FIELD_ANALYTICS_LABELS[name as keyof FormValues];
                if (label) {
                    analytics.event({ category: "partners_form", action: "changed", label });
                    changedFields.current.add(name);
                }
            }
        });
        return () => subscription.unsubscribe();
    }, [watch]);

    async function onSubmit(data: FormValues): Promise<void> {
        analytics.event({ category: "partners_form", action: "submitted" });
        setShowErrorModal(false);

        try {
            const body = new URLSearchParams({
                name: data.name,
                email: data.email,
                company: data.company,
                message: data.message,
                hp_field: data.hp_field ?? "",
            });

            const response = await requester.post<{ success?: boolean } | string>(FORM_URL, body);

            if (typeof response === "object" && response !== null && response.success === false) {
                throw new Error("Partners form submission was unsuccessful.");
            }

            toast({ message: gettext("partners.contact.success_toast") });
        } catch (err: any) {
            logging.reportError(err instanceof Error ? err : new Error(String(err)));
            setShowErrorModal(true);
        }
    }

    return (
        <section ref={sectionRef} id="contact-form" className={styles.section}>
            <div className={styles.contentContainer}>
                <div className={styles.contentInner}>
                    <form onSubmit={handleSubmit(onSubmit)} noValidate>
                        <div className={styles.formLayout}>
                            <div className={styles.formHeader}>
                                <Heading textStyle="hero-l" as="h2">
                                    {gettext("partners.contact.headline")}
                                </Heading>
                                <Text textStyle="body" color="secondary">
                                    {gettext("partners.contact.subheadline")}
                                </Text>
                            </div>

                            <div className={styles.formFields}>
                                <div className={styles.fieldRow}>
                                    <TextInput
                                        id="contact-name"
                                        label={gettext(FIELD_LABELS.name)}
                                        autoComplete="name"
                                        error={!!errors.name}
                                        errorMessage={errors.name?.message}
                                        {...register("name", {
                                            required: gettext(
                                                "form_input.validation.value_missing",
                                                {
                                                    field_name: gettext(FIELD_LABELS.name),
                                                }
                                            ),
                                        })}
                                    />
                                    <TextInput
                                        id="contact-email"
                                        label={gettext(FIELD_LABELS.email)}
                                        autoComplete="email"
                                        type="email"
                                        error={!!errors.email}
                                        errorMessage={errors.email?.message}
                                        {...register("email", {
                                            required: gettext(
                                                "form_input.validation.value_missing",
                                                {
                                                    field_name: gettext(FIELD_LABELS.email),
                                                }
                                            ),
                                            pattern: {
                                                value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                                                message: gettext("form_input.validation.invalid", {
                                                    field_name: gettext(FIELD_LABELS.email),
                                                }),
                                            },
                                        })}
                                    />
                                </div>
                                <TextInput
                                    id="contact-company"
                                    label={gettext(FIELD_LABELS.company)}
                                    autoComplete="organization"
                                    error={!!errors.company}
                                    errorMessage={errors.company?.message}
                                    {...register("company", {
                                        required: gettext("form_input.validation.value_missing", {
                                            field_name: gettext(FIELD_LABELS.company),
                                        }),
                                    })}
                                />
                                <div>
                                    <label
                                        htmlFor="contact-message"
                                        className={styles.textareaLabel}
                                    >
                                        {gettext(FIELD_LABELS.message)}
                                    </label>
                                    <textarea
                                        id="contact-message"
                                        placeholder={gettext(FIELD_LABELS.message)}
                                        className={`${styles.textarea}${
                                            errors.message ? ` ${styles.textareaError}` : ""
                                        }`}
                                        {...register("message", {
                                            required: gettext(
                                                "form_input.validation.value_missing",
                                                {
                                                    field_name: gettext(FIELD_LABELS.message),
                                                }
                                            ),
                                        })}
                                    />
                                    {errors.message && (
                                        <span className={styles.textareaErrorMessage}>
                                            {errors.message.message}
                                        </span>
                                    )}
                                </div>
                                <input
                                    type="text"
                                    tabIndex={-1}
                                    autoComplete="off"
                                    aria-hidden="true"
                                    className={styles.honeypotField}
                                    {...register("hp_field")}
                                />
                            </div>

                            <div className={styles.submitRow}>
                                <Button
                                    variant="primary"
                                    title={gettext("general.submit")}
                                    type="submit"
                                    inactive={!isValid || isSubmitting}
                                />
                            </div>
                        </div>
                    </form>
                </div>
            </div>

            <div className={styles.imageContainer}>
                <img src={formImage} alt="" className={styles.image} loading="lazy" />
            </div>

            <CheckoutErrorModal isOpen={showErrorModal} onClose={() => setShowErrorModal(false)} />
        </section>
    );
}
